import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, X, List, TreeDeciduous } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Badges";
import { BranchTree } from "@/components/tree/BranchTree";
import { NodeDetail } from "@/components/tree/NodeDetail";
import { TreeProposals } from "@/components/tree/TreeProposals";
import { TreeMap } from "@/components/tree/TreeMap";
import { FileCard } from "@/components/tree/FileCard";
import { Input } from "@/components/ui/input";
import { t } from "@/lib/i18n";

export default function TreePage({ view = "list" }) {
  const [tree, setTree] = useState([]);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const selected = params.get("node");
  const selectedFile = params.get("file");
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [maturity, setMaturity] = useState("");
  const panelRef = useRef(null);
  const firstRender = useRef(true);

  const loadTree = () => api.get("/tree").then((r) => setTree(r.data));
  useEffect(() => { loadTree(); }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setResults(r.data)), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (view === "map" && (selected || selectedFile)) panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selected, selectedFile, view]);

  const select = (id) => { setParams(id ? { node: id } : {}); setResults(null); setQ(""); };
  const selectFile = (id) => { setParams({ file: id }); };
  const switchView = (v) => navigate((v === "map" ? "/mappa" : "/") + (selected ? `?node=${selected}` : ""));
  const total = tree.reduce((a, b) => a + b.children.length, 0);

  return (
    <div data-testid="tree-page">
      <PageHeader eyebrow={t("Corpus conoscitivo · Food & Beverage Wine Management")} title={t("Albero decisionale")}
        subtitle={t("{n} rami, {m} sotto-rami. Ogni nodo separa teoria, dato operativo e strumento decisionale, dichiara la propria maturità e collega i file fonte.", { n: tree.length, m: total })}>
        <div className="relative w-full lg:w-96">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="tree-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Cerca concetti, nodi, file…")} className="pl-9 bg-card" />
          {q && <button data-testid="tree-search-clear" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"><X size={14} /></button>}
          {results && (
            <div data-testid="tree-search-results" className="absolute z-40 mt-2 w-full surface-elevated shadow-2xl max-h-80 overflow-auto p-2">
              {results.nodes.length === 0 && results.files.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">{t("Nessun risultato")}</div>}
              {results.nodes.map((n) => (
                <button key={n.id} data-testid={`search-result-${n.id}`} onClick={() => select(n.id)} className="w-full text-left px-3 py-2 rounded-md hover:bg-accent flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: n.color }} />
                  <span className="text-sm truncate">{n.title}</span>
                  <span className="ml-auto text-[10px] uppercase text-muted-foreground">L{n.level}</span>
                </button>
              ))}
              {results.files.map((f) => (
                <button key={f.id} onClick={() => select(f.primary_branch)} className="w-full text-left px-3 py-2 rounded-md hover:bg-accent text-sm text-muted-foreground truncate">📄 {f.name}</button>
              ))}
            </div>
          )}
        </div>
      </PageHeader>

      <div className="px-5 lg:px-8 pt-4"><TreeProposals onApplied={(id) => { loadTree(); select(id); }} /></div>

      <div className="px-5 lg:px-8 py-4 flex flex-wrap items-center gap-2">
        <div data-testid="tree-view-toggle" className="flex p-1 bg-card rounded-md border border-border mr-3">
          <button data-testid="tree-view-list" onClick={() => switchView("list")} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}><List size={13} /> {t("Elenco")}</button>
          <button data-testid="tree-view-map" onClick={() => switchView("map")} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded ${view === "map" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}><TreeDeciduous size={13} /> {t("Mappa")}</button>
        </div>
        <span className="eyebrow mr-1">{t("Filtro maturità")}</span>
        {[["", "Tutti"], ["consolidato", "Consolidato"], ["parziale", "Parziale"], ["da_approfondire", t("Da approfondire")]].map(([v, l]) => (
          <button key={v} data-testid={`maturity-filter-${v || "all"}`} onClick={() => setMaturity(v)}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${maturity === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {view === "map" ? (
        <div className="px-5 lg:px-8 pb-8 space-y-6">
          <TreeMap tree={tree} selected={selected} selectedFile={selectedFile} onSelect={select} onSelectFile={selectFile} maturity={maturity} />
          <div ref={panelRef} className="scroll-mt-4 grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
            {selectedFile ? <FileCard id={selectedFile} tree={tree} onSelectNode={select} /> : selected ? <NodeDetail id={selected} onSelect={select} /> : (
              <div data-testid="map-detail-empty" className="surface p-8 text-center text-muted-foreground">
                <div className="font-heading text-lg text-foreground mb-2">{t("Tocca un punto dell'albero")}</div>
                {t("I cerchi numerati sono gli 8 rami, le foglie i sotto-rami, i punti sotto terra le dispense che nutrono il metodo. Selezionandone uno, qui si apre la sua scheda.")}
              </div>
            )}
            <div className="surface p-5 text-sm text-muted-foreground space-y-3 self-start">
              <div className="eyebrow">{t("Come leggere la mappa")}</div>
              <p><span className="text-foreground font-medium">{t("Radici")}</span>{t(": i documenti caricati nel corpus. Più radici ha un ramo, più la sua conoscenza è fondata.")}</p>
              <p><span className="text-foreground font-medium">{t("Rami")}</span>{t(": le 8 aree del metodo, un colore ciascuna.")}</p>
              <p><span className="text-foreground font-medium">{t("Foglie")}</span>{t(": i sotto-rami. Grande e piena = consolidato, media = parziale, piccola e vuota = da approfondire.")}</p>
            </div>
          </div>
        </div>
      ) : (
      <div className="px-5 lg:px-8 pb-8 grid grid-cols-1 xl:grid-cols-[440px_1fr] gap-6">
        <BranchTree tree={tree} selected={selected} onSelect={select} maturity={maturity} />
        <div className="xl:sticky xl:top-6 self-start">
          {selected ? <NodeDetail id={selected} onSelect={select} /> : (
            <div data-testid="node-detail-empty" className="surface p-8 text-center text-muted-foreground">
              <div className="font-heading text-lg text-foreground mb-2">{t("Seleziona un nodo")}</div>
              {t("Espandi un ramo e scegli un sotto-ramo o un concetto per leggere sintesi, KPI, file fonte e collegamenti trasversali.")}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
